import type { Carrier } from '@klareospace/pharos-lib-types';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import type { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod/v4';

import { useCreateCarrier } from './use-create-carrier';
import { useUpdateCarrier } from './use-update-carrier';
import type { NationalCarrierFormInput } from '../types';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface UseNationalCarrierFormReturn {
  form: UseFormReturn<NationalCarrierFormInput>;
  isPending: boolean;
  onSubmit: (data: NationalCarrierFormInput) => void;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getFormDefaults(carrier: Carrier | null): NationalCarrierFormInput {
  if (carrier === null) {
    return { code: '', name: '' };
  }

  return { code: carrier.code, name: carrier.name };
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useNationalCarrierForm(carrier: Carrier | null, onSuccess: () => void): UseNationalCarrierFormReturn {
  const { t } = useTranslation('settings');
  const createCarrier = useCreateCarrier();
  const updateCarrier = useUpdateCarrier();

  const isEditMode = carrier !== null;

  const schema = useMemo(
    () =>
      z.object({
        code: z.string().trim().min(1, t('nationalCarriers.drawer.fields.code.required')),
        name: z.string().trim().min(1, t('nationalCarriers.drawer.fields.name.required')),
      }),
    [t],
  );

  const form = useForm<NationalCarrierFormInput>({
    defaultValues: getFormDefaults(carrier),
    mode: 'onChange',
    resolver: zodResolver(schema),
  });

  // Reset only when the carrier id changes (or goes null→carrier / carrier→null) — not on refetch reference changes
  useEffect(() => {
    form.reset(getFormDefaults(carrier));
  }, [carrier?.id]);

  // Errors are surfaced by the mutation hooks' onError callbacks.
  function onSubmit(data: NationalCarrierFormInput): void {
    if (isEditMode) {
      updateCarrier.mutate({ data: { code: data.code, name: data.name }, id: carrier.id }, { onSuccess });
    } else {
      createCarrier.mutate(
        { code: data.code, enabledScopes: [], name: data.name },
        {
          onSuccess: () => {
            toast.success(t('nationalCarriers.drawer.create.success'));
            form.reset();
            onSuccess();
          },
        },
      );
    }
  }

  return {
    form,
    isPending: createCarrier.isPending || updateCarrier.isPending,
    onSubmit,
  };
}
